// app.config

import {
  APP_ID,
  ApplicationConfig,
  FactoryProvider,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  TransferState,
  ValueProvider,
} from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withNoIncrementalHydration,
} from '@angular/platform-browser';
import {
  AppConfigService,
  configKey,
  FULL_STORAGE_URL,
  SiteTitleService,
  STORAGE_FILE_DOWNLOAD_URL_GETTER,
  STORAGE_FILE_URL_GETTER,
  STORAGE_URL,
  StorageFileUrlGetterFn,
} from '@math-site/services';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MathConfiguration, SiteSettingsApiClient } from '@math-faculty/api-client';
import { ErrorResponseService } from '@math-site/site-services';

import { AppRoutingModule } from './app-routing.module';

export function storageFileUrlGetterFactory(storageUrl: string): StorageFileUrlGetterFn {
  return (id) => `${storageUrl}${id}`;
}

export function storageFileDownloadUrlGetterFactory(storageUrl: string): StorageFileUrlGetterFn {
  return (id) => `${storageUrl}/file/download/${id}`;
}

export function fullStorageUrlFactory(storageUrl: string): string {
  return `${storageUrl}/file/get/`;
}

export function provideStorageUrl(): FactoryProvider {
  return {
    provide: STORAGE_URL,
    deps: [AppConfigService],
    useFactory: (cfg: AppConfigService) => cfg.get()?.storageUrl,
  };
}

export function provideFullStorageUrl(): FactoryProvider {
  return {
    provide: FULL_STORAGE_URL,
    deps: [STORAGE_URL],
    useFactory: fullStorageUrlFactory,
  };
}

export function provideStorageFileUrlGetter(): FactoryProvider {
  return {
    provide: STORAGE_FILE_URL_GETTER,
    deps: [FULL_STORAGE_URL],
    useFactory: storageFileUrlGetterFactory,
  };
}

export function provideFileDownloadUrlGetter(): FactoryProvider {
  return {
    provide: STORAGE_FILE_DOWNLOAD_URL_GETTER,
    deps: [STORAGE_URL],
    useFactory: storageFileDownloadUrlGetterFactory,
  };
}

export function provideRuLocale(): ValueProvider {
  return {
    provide: LOCALE_ID,
    useValue: 'ru-RU',
  };
}

export function provideAppId(): ValueProvider {
  return { provide: APP_ID, useValue: 'math-ssr' };
}

export function provideApi(): FactoryProvider {
  return {
    provide: MathConfiguration,
    useFactory: () => {
      const cfg = inject(AppConfigService).get();

      return new MathConfiguration({
        basePath: cfg.api,
      });
    },
    multi: false,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppId(),
    importProvidersFrom(BrowserModule),
    AppConfigService,
    provideAppInitializer(() => {
      const cfg = inject(TransferState).get(configKey, undefined);

      return inject(AppConfigService).init$(cfg);
    }),
    provideStorageUrl(),
    provideFullStorageUrl(),
    provideStorageFileUrlGetter(),
    provideFileDownloadUrlGetter(),
    provideRuLocale(),
    provideClientHydration(
      withHttpTransferCacheOptions({ includePostRequests: true }),
      withEventReplay(),
      withNoIncrementalHydration(),
    ),
    provideHttpClient(withFetch()),
    provideAnimations(),
    provideApi(),

    SiteSettingsApiClient,
    SiteTitleService,
    ErrorResponseService,
    importProvidersFrom(AppRoutingModule),
  ],
};
